// ===== BEGIN FILE: src/utils/chatContext.ts =====
/**
 * Chat Context Builder for AI Assistant
 * 
 * Assembles structured astrological data for GPT-5 consumption.
 * Uses multi-tier strategy to optimize token usage:
 * - Tier 1: Core chart (always included)
 * - Tier 2: Intent-based (selective based on user query)
 * - Tier 3: On-demand (not sent, AI references from memory)
 */

import type { PlanetName } from './astroEngine';
import { RASHI_NAMES, NAKSHATRA_NAMES, getPlanetDignity } from './astroEngine';
import type { FeedItem, DashaContext, HouseNum } from '../astro/feeds/types';
import type { VargaKey, LifeAreaKey } from './lifeAreas';
import { LIFE_AREAS, getVargaDescription } from './lifeAreas';
import { SIGN_LORDS, getHousesRuledByPlanet } from './lordshipUtils';

// ===== TYPES =====

export interface ChatSourceData {
  // Core astro data from App state
  astroData: {
    asc: {
      ascendantSign: string;
      ascendantSignIndex: number;
      ascendantNakshatra: string;
      siderealLongitudeDeg: number;
    };
    moon: {
      nakshatra: string;
      moonSign: string;
      moonSignIndex: number;
      siderealLongitudeDeg: number;
    };
    grahas: Partial<Record<PlanetName, { lon: number; retrograde: boolean }>>;
    nodes: { Rahu: number; Ketu: number };
    placements: Partial<Record<PlanetName, { house: number; offsetDeg: number }>>;
    aspects: Array<{ from: string; toHouse: number; mode: string }>;
    cusps: number[];
  };
  
  // Divisional charts (vargas) for specialized analysis
  vargaData?: Partial<Record<VargaKey, {
    ascSign: number; // 1-12
    planetSigns: Partial<Record<PlanetName, number>>; // 1-12 per planet
    placements: Partial<Record<PlanetName, { house: number; offsetDeg: number }>>;
  }>>;
  
  // Planet strength metrics
  blendedPlanetPct?: Partial<Record<PlanetName, number>>; // -100 to +100
  shadbalaPct?: any; // Shadbala percentages
  bhavaBalaRupas?: Record<number, number>; // House strength percentages
  
  // Dignities and afflictions
  combust?: Partial<Record<PlanetName, { combust: boolean; degreesFromSun?: number }>>;
  debil?: Partial<Record<PlanetName, boolean>>;
  exalt?: Partial<Record<PlanetName, boolean>>;
  
  // House scores and interpretations
  d1HouseScores?: Record<number, number>; // -100 to +100
  /** Pre-computed dasha impact per house (0-100, 50=neutral) — matches Dashboard exactly */
  dashaImpactByHouse?: Record<number, number>;
  /** Pre-computed transit impact per house (0-100, 50=neutral) — matches Dashboard exactly */
  transitImpactByHouse?: Record<number, number>;
  /** Karmic friction scores per planet (0-100) — used to compute per-house Life Friction Index */
  frictionScoresByPlanet?: Record<string, number>;
  /** Activities to balance each house — from Supabase houses.activities column (keyed by house 1-12) */
  houseActivities?: Record<number, string>;
  houseInterpretations?: Array<{
    house: number;
    overview?: string;
    signNote?: string; // Sign-in-house note
    planets?: Array<{ 
      planet: string; 
      interpretation?: string;
      nakshatraNote?: string;
      housePlanetNote?: string;
    }>;
    lordship?: Array<{ 
      lord: string; 
      position: string; 
      interpretation?: string;
      overview?: string;
      strengths?: string;
      challenges?: string;
      remedies?: string;
    }>;
  }>;
  
  // Dasha and timing
  dashaContext?: DashaContext | {
    MD: { planet: string; occupies: number; rules: number[]; aspects: number[]; from: string; to: string };
    AD: { planet: string; occupies: number; rules: number[]; aspects: number[]; from: string; to: string };
    PD: { planet: string; occupies: number; rules: number[]; aspects: number[]; from: string; to: string };
    SD: { planet: string; occupies: number; rules: number[]; aspects: number[]; from: string; to: string };
    window: { from: string; to: string };
  } | null;
  feeds?: FeedItem[];
  yogas?: Array<{
    lifeArea: string;
    severity: string;
    score: number;
    message: string;
    window?: string;
    evidence?: string[];
  }>;
  
  // Transit data (NEW)
  transitData?: {
    date: string; // Current transit date
    placements: Partial<Record<PlanetName, { house: number; offsetDeg: number }>>;
    longitudes: Partial<Record<PlanetName, number>>;
    dignities?: Partial<Record<PlanetName, string>>; // exalted/moolatrikona/own/friend/neutral/enemy/debilitated
    retrogrades?: Partial<Record<string, boolean>>; // planet → retrograde status
    combust?: Partial<Record<string, boolean>>;     // planet → combust by transit Sun
    // Sign ingress/egress dates per planet (Moon excluded — too fast)
    sunSignDates?:     { currentSign: number; enteredOn: string; exitsOn: string; nextSign: number };
    marsSignDates?:    { currentSign: number; enteredOn: string; exitsOn: string; nextSign: number };
    mercurySignDates?: { currentSign: number; enteredOn: string; exitsOn: string; nextSign: number };
    venusSignDates?:   { currentSign: number; enteredOn: string; exitsOn: string; nextSign: number };
    jupiterSignDates?: { currentSign: number; enteredOn: string; exitsOn: string; nextSign: number };
    saturnSignDates?:  { currentSign: number; enteredOn: string; exitsOn: string; nextSign: number };
    rahuSignDates?:    { currentSign: number; enteredOn: string; exitsOn: string; nextSign: number };
    ketuSignDates?:    { currentSign: number; enteredOn: string; exitsOn: string; nextSign: number };
  };
  
  // Yogas and special conditions (NEW)
  sadeSati?: {
    isActive: boolean;
    currentPhase?: 'rising' | 'peak' | 'setting';
    from?: string;
    to?: string;
    moonSign: number;
    saturnSign?: number; // Current Saturn transit sign 1-12
    allPhases?: Array<{ phase: 'rising' | 'peak' | 'setting'; from: string; to: string }>;
    totalFrom?: string; // Start of full 7.5-year period
    totalTo?: string;   // End of full 7.5-year period
  };
  kaalSarpYoga?: {
    isPresent: boolean;
    type: 'partial' | 'full' | 'none';
    details: string;
    specificType?: string;          // e.g. Anant, Kulik, Vasuki
    specificEffects?: string;       // Classical effects of the specific type
    rahuHouse?: number;
    ketuHouse?: number;
    planetsBetween?: string[];
    hasRajYogaCombination?: boolean;
  };
  conjunctions?: Array<{ house: number; planets: string[] }>; // Houses with 2+ planets
  functionalNature?: Partial<Record<PlanetName, 'benefic' | 'malefic' | 'neutral' | 'yoga_karaka'>>;
  lordDusthanaPenalties?: Record<number, { penalty: number; severity: string; reason: string; lord: string; lordHouse: number }>;
  /** House lords from d1HouseInfluence — same source as RemediesModal (house 1-12 → planet name) */
  houseLords?: Record<number, string>;
  /** Aspecting planets per house from d1HouseInfluence — same source as RemediesModal */
  houseAspects?: Record<number, string[]>;
  
  // Pre-built combination/conjunction prediction lines (subset of feeds with IS_COMBO ruleKeys)
  combinationPredictions?: string[];
  // Dasha-lord remedies fetched from DB for current malefic PD/SD lords
  dashaLordRemedies?: Array<{ planet: string; title: string; description: string; category: string }>;
  
  // Metadata
  utcDateTime?: string;
  selectedCity?: { label: string; lat: number; lon: number; tz: string };
  ayanamsa?: string;
  gender?: 'male' | 'female' | 'other';
  name?: string;    // First name of the native
  ageYears?: number; // Computed age from birth date
}

export interface ChatContextOptions {
  includeFeeds?: boolean;
  includeInterpretations?: boolean;
  maxInterpretationLength?: number; // Truncate long interpretations
  intent?: 'career' | 'health' | 'relationships' | 'wealth' | 'dasha' | 'transit' | 'general';
  lifeArea?: LifeAreaKey; // Filter context by specific life area
  includeVargas?: boolean; // Include divisional chart data (default: true when lifeArea is set)
  lifeAreaRemedies?: Array<{ title: string; description: string; category: string; planet: string }>; // Mandatory/important remedies for specific life area
}

export interface ChatContext {
  meta: {
    ascSign: number;
    ascSignName: string;
    moonSign: number;
    moonSignName: string;
    moonNakshatra: string;
    ascNakshatra: string;
    ayanamsa: string;
    birthDate?: string;
    location?: string;
    gender?: string;
    name?: string;
    ageYears?: number;
  };
  
  planets: Array<{
    name: string;
    house: number;
    sign: number;
    longitude: number;
    nakshatra?: string;
    retrograde: boolean;
    // Strength & dignity
    blendedStrength?: number; // -100 to +100
    combust?: boolean;
    debilitated?: boolean;
    exalted?: boolean;
    natalDignity?: string; // exalted/moolatrikona/own/friend/neutral/enemy/debilitated
    // Lordship
    rules?: number[]; // Houses ruled
    // Proprietary interpretation (NEW)
    interpretation?: string; // Planet-in-house prediction from database
  }>;
  
  houses: Array<{
    house: number;
    sign: number;
    signName: string;
    strength?: number; // -100 to +100 from bhavaBala or d1HouseScores
    residentPlanets: string[];
    aspectingPlanets: string[]; // Planets aspecting this house
    lord: string; // Sign lord
    lordHouse?: number; // Where the lord is placed
    overview?: string; // House overview interpretation (tier 2)
    signNote?: string; // Sign-in-house note (NEW)
    planetInterpretations?: Array<{ 
      planet: string; 
      text: string;
      nakshatraNote?: string; // Nakshatra-specific interpretation (NEW)
      housePlanetNote?: string; // House-specific planet note (NEW)
    }>; // Planet-in-house predictions (NEW)
    lordshipInterpretations?: Array<{ 
      lord: string; 
      position: string; 
      text: string;
      overview?: string; // Lord-in-house overview (NEW)
      strengths?: string; // Strengths (NEW)
      challenges?: string; // Challenges (NEW)
      remedies?: string; // Remedies (NEW)
    }>; // Lord-in-house predictions (NEW)
  }>;
  
  remedies?: Array<{ // Mandatory/important remedies for life area (NEW)
    title: string;
    description: string;
    category: string;
    planet: string;
  }>;
  
  dashas?: {
    MD: { 
      planet: string; 
      occupies: number; 
      rules: number[]; 
      aspects: number[]; 
      from: string; 
      to: string;
      transitStats?: {
        beneficSupport: number;  // 0-100%
        maleficPressure: number; // 0-100%
        avgTransits: number;     // avg # of planets transiting target houses
      };
    };
    AD: { 
      planet: string; 
      occupies: number; 
      rules: number[]; 
      aspects: number[]; 
      from: string; 
      to: string;
      transitStats?: {
        beneficSupport: number;  // 0-100%
        maleficPressure: number; // 0-100%
        avgTransits: number;     // avg # of planets transiting target houses
      };
    };
    PD: { planet: string; occupies: number; rules: number[]; aspects: number[]; from: string; to: string };
    SD: { planet: string; occupies: number; rules: number[]; aspects: number[]; from: string; to: string };
    window: { from: string; to: string };
  };
  
  feeds?: Array<{
    lifeArea: string;
    severity: string;
    score: number;
    message: string;
    window: { from: string; to: string };
    ruleKey?: string;
  }>;
  
  aspects: Array<{
    from: string;
    toHouse: number;
  }>;
  
  // Transit data (NEW)
  transits?: {
    date: string;
    planets: Array<{
      name: string;
      house: number; // Transit house in natal chart
      sign: number;
      longitude: number;
      natalHouse: number; // For comparison
      transitDignity?: string; // exalted/moolatrikona/own/friend/neutral/enemy/debilitated
      retrograde?: boolean;
      combust?: boolean;
    }>;
    // Sign ingress/egress date windows (slow planets)
    sunSignDates?:     { currentSign: number; enteredOn: string; exitsOn: string; nextSign: number };
    marsSignDates?:    { currentSign: number; enteredOn: string; exitsOn: string; nextSign: number };
    mercurySignDates?: { currentSign: number; enteredOn: string; exitsOn: string; nextSign: number };
    venusSignDates?:   { currentSign: number; enteredOn: string; exitsOn: string; nextSign: number };
    jupiterSignDates?: { currentSign: number; enteredOn: string; exitsOn: string; nextSign: number };
    saturnSignDates?:  { currentSign: number; enteredOn: string; exitsOn: string; nextSign: number };
    rahuSignDates?:    { currentSign: number; enteredOn: string; exitsOn: string; nextSign: number };
    ketuSignDates?:    { currentSign: number; enteredOn: string; exitsOn: string; nextSign: number };
  };
  
  // Yogas and special conditions (NEW)
  sadeSati?: {
    isActive: boolean;
    phase?: 'rising' | 'peak' | 'setting';
    from?: string;
    to?: string;
    moonSign?: number;
    saturnSign?: number;
    allPhases?: Array<{ phase: 'rising' | 'peak' | 'setting'; from: string; to: string }>;
    totalFrom?: string;
    totalTo?: string;
  };
  
  kaalSarpYoga?: {
    isPresent: boolean;
    type: string;
    details: string;
    specificType?: string;
    specificEffects?: string;
    rahuHouse?: number;
    ketuHouse?: number;
    planetsBetween?: string[];
    hasRajYogaCombination?: boolean;
  };
  
  // Natal conjunctions — houses with 2+ planets co-tenant
  conjunctions?: Array<{ house: number; planets: string[] }>;

  // Lord dusthana afflictions — adjusted house scores due to lord in 6th/8th/12th from own house
  houseAfflictions?: Array<{
    house: number;
    lord: string;
    lordHouse: number;
    penalty: number;
    severity: string;
    reason: string;
    /** True when Jupiter aspects the owned house — challenges softened, recovery assured */
    jupiterProtected?: boolean;
  }>;
  
  /** Pre-computed dasha impact per house (0-100, 50=neutral) — matches Dashboard exactly */
  dashaImpactByHouse?: Record<number, number>;
  /** Pre-computed transit impact per house (0-100, 50=neutral) — matches Dashboard exactly */
  transitImpactByHouse?: Record<number, number>;
  /** Karmic friction scores per planet (0-100) — Life Friction Index */
  frictionScoresByPlanet?: Record<string, number>;
  /** Recommended activities to balance each house — from Supabase houses.activities column */
  houseActivities?: Record<number, string>;

  // Pre-built planetary combination prediction lines
  combinationPredictions?: string[];
  // Dasha-lord remedies from DB
  dashaLordRemedies?: Array<{ planet: string; title: string; description: string; category: string }>;

  // Jaimini Chara Karakas
  charaKarakas?: {
    AK: string;  // Atma Karaka — soul significator
    AmK: string; // Amatya Karaka — career/mind
    BK: string;  // Bhratri Karaka — siblings
    MK: string;  // Matri Karaka — mother
    PuK: string; // Putra Karaka — children
    GK: string;  // Gnati Karaka — relatives/enemies
    DK: string;  // Dara Karaka — spouse
  };
  
  functionalNature?: {
    benefics: string[];
    malefics: string[];
    yogaKarakas: string[];
  };
  
  // Divisional charts (vargas) for specialized analysis (NEW)
  vargas?: Array<{
    chart: VargaKey;
    description: string;
    ascSign: number;
    ascSignName: string;
    planets: Array<{
      name: string;
      house: number;
      sign: number;
      signName: string;
      dignity?: string; // exalted/debilitated/own/friend/enemy/neutral
    }>;
  }>;
  
  // Life area focus metadata (NEW)
  lifeAreaContext?: {
    area: LifeAreaKey;
    label: string;
    description: string;
    relevantHouses: number[];
    relevantVargas: VargaKey[];
  };
}

// ===== CONSTANTS =====

/** Nakshatra ruling planets (27 nakshatras in order, Ashwini = index 0) */
const NAKSHATRA_LORDS: Record<string, string> = {
  Ashwini: 'Ketu', Bharani: 'Venus', Krittika: 'Sun',
  Rohini: 'Moon', Mrigashira: 'Mars', Ardra: 'Rahu',
  Punarvasu: 'Jupiter', Pushya: 'Saturn', Ashlesha: 'Mercury',
  Magha: 'Ketu', Purva_Phalguni: 'Venus', Uttara_Phalguni: 'Sun',
  Hasta: 'Moon', Chitra: 'Mars', Swati: 'Rahu',
  Vishakha: 'Jupiter', Anuradha: 'Saturn', Jyeshtha: 'Mercury',
  Mula: 'Ketu', Purva_Ashadha: 'Venus', Uttara_Ashadha: 'Sun',
  Shravana: 'Moon', Dhanishtha: 'Mars', Shatabhisha: 'Rahu',
  Purva_Bhadrapada: 'Jupiter', Uttara_Bhadrapada: 'Saturn', Revati: 'Mercury'
};

// Map NAKSHATRA_NAMES index → lord for quick lookup
const NAKSHATRA_LORD_BY_INDEX: string[] = [
  'Ketu','Venus','Sun','Moon','Mars','Rahu','Jupiter','Saturn','Mercury',
  'Ketu','Venus','Sun','Moon','Mars','Rahu','Jupiter','Saturn','Mercury',
  'Ketu','Venus','Sun','Moon','Mars','Rahu','Jupiter','Saturn','Mercury'
];

const CHARA_KARAKA_NAMES = ['AK','AmK','BK','MK','PuK','GK','DK'] as const;

/** Parashara aspect offsets per planet (1-indexed offset positions) */
const ASPECT_OFFSETS: Record<string, number[]> = {
  Sun: [7], Moon: [7], Mercury: [7], Venus: [7],
  Mars: [4, 7, 8],
  Jupiter: [5, 7, 9],
  Saturn: [3, 7, 10],
  Rahu: [5, 7, 9],
  Ketu: [5, 7, 9]
};

// ===== HELPER FUNCTIONS =====

function getNakshatraFromLongitude(lon: number): string {
  const normalized = ((lon % 360) + 360) % 360;
  const nakIndex = Math.floor(normalized / (360 / 27));
  return NAKSHATRA_NAMES[nakIndex] || 'Unknown';
}

function getSignFromLongitude(lon: number): number {
  const normalized = ((lon % 360) + 360) % 360;
  return Math.floor(normalized / 30) + 1;
}

function truncateText(text: string | undefined, maxLength: number): string | undefined {
  if (!text) return text;
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '... [truncated]';
}

/**
 * Calculate Jaimini Chara Karakas for the 7 classical planets.
 * Karakas are assigned by degree-within-sign (descending):
 * highest = AK (Atma Karaka), lowest = DK (Dara Karaka).
 */
function computeCharaKarakas(
  grahas: Partial<Record<PlanetName, { lon: number; retrograde: boolean }>>
): Record<string, string> | undefined {
  const sevenPlanets: PlanetName[] = ['Sun', 'Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn'];
  
  const withDegrees: Array<{ planet: PlanetName; degInSign: number }> = [];
  sevenPlanets.forEach(p => {
    const graha = grahas[p];
    if (!graha) return;
    const degInSign = graha.lon % 30; // 0–30
    withDegrees.push({ planet: p, degInSign });
  });
  
  if (withDegrees.length < 7) return undefined;
  
  // Sort descending by degree-within-sign
  withDegrees.sort((a, b) => b.degInSign - a.degInSign);
  
  const result: Record<string, string> = {};
  CHARA_KARAKA_NAMES.forEach((kName, idx) => {
    if (withDegrees[idx]) {
      result[kName] = withDegrees[idx].planet;
    }
  });
  return result;
}

/**
 * Get the nakshatra lord from a nakshatra name string
 */
function getNakshatraLord(nakshatraName: string): string | undefined {
  // Try direct lookup (with spaces replaced by underscores)
  const key = nakshatraName.replace(/ /g, '_') as keyof typeof NAKSHATRA_LORDS;
  if (NAKSHATRA_LORDS[key]) return NAKSHATRA_LORDS[key];
  // Fallback: index from NAKSHATRA_NAMES
  const idx = (NAKSHATRA_NAMES as readonly string[]).indexOf(nakshatraName);
  if (idx >= 0) return NAKSHATRA_LORD_BY_INDEX[idx];
  return undefined;
}

/**
 * Calculate which house a planet aspects given from house and planet name.
 * Returns target house numbers (1-12).
 */
function getAspectedHouses(planet: string, fromHouse: number): number[] {
  const offsets = ASPECT_OFFSETS[planet] || [7];
  return offsets.map(offset => {
    let h = fromHouse + offset - 1;
    if (h > 12) h -= 12;
    if (h < 1) h += 12;
    return h;
  });
}

/**
 * Detect life area from message keywords (returns LifeAreaKey or null).
 * Exported for use in ChatPanel.tsx.
 */
export function detectLifeAreaFromMessage(message: string): LifeAreaKey | null {
  const lower = message.toLowerCase();

  // Marriage / relationship / spouse
  if (/\b(marr|spouse|partner|wedding|husband|wife|relation|love|divorce|separat)\w*/.test(lower)) return 'marriage';
  // Career / job / profession
  if (/\b(career|job|work|profession|employ|promot|salary|boss|office|occupation)\w*/.test(lower)) return 'job';
  if (/\b(business|entrepreneur|startup|venture|trade|commerce)\w*/.test(lower)) return 'business';
  // Finance / wealth / money
  if (/\b(money|wealth|financ|income|earning|profit|saving|investment|debt|loan|rich)\w*/.test(lower)) return 'wealth';
  // Children / pregnancy / progeny
  if (/\b(child|children|pregnan|baby|son|daughter|progeny|fertilit|motherhood|fatherhood)\w*/.test(lower)) return 'children';
  // Property / real estate / home
  if (/\b(propert|house|home|real estate|flat|apartment|land|buy|sell.*home|own.*home)\w*/.test(lower)) return 'property';
  // Health
  if (/\b(health|ill|disease|sick|medical|hospital|surgery|recover|pain|diagnos)\w*/.test(lower)) return 'health';
  // Education / studies
  if (/\b(study|studies|education|school|college|university|degree|exam|academ|learn)\w*/.test(lower)) return 'education';
  // Spirituality — no matching LifeAreaKey, return null
  // Travel — no matching LifeAreaKey, return null
  // Father / mother / parents
  if (/\b(father|dad|paternal|parent)\w*/.test(lower)) return 'father';
  if (/\b(mother|mom|maternal)\w*/.test(lower)) return 'mother';
  // Siblings — no matching LifeAreaKey, return null

  return null;
}

// ===== MAIN CONTEXT BUILDER =====

export function buildChatContext(
  data: ChatSourceData,
  opts: ChatContextOptions = {}
): ChatContext {
  const {
    includeFeeds = true,
    includeInterpretations = false,
    maxInterpretationLength = 300,
    intent,
    lifeAreaRemedies
  } = opts;

  const { astroData, blendedPlanetPct, bhavaBalaRupas, combust, debil, exalt,
    d1HouseScores, houseInterpretations, dashaContext, feeds,
    utcDateTime, selectedCity, ayanamsa, gender, name, ageYears } = data;

  // ===== META =====
  const meta = {
    ascSign: astroData.asc.ascendantSignIndex,
    ascSignName: RASHI_NAMES[astroData.asc.ascendantSignIndex - 1] || 'Unknown',
    moonSign: astroData.moon.moonSignIndex,
    moonSignName: astroData.moon.moonSign,
    moonNakshatra: astroData.moon.nakshatra,
    ascNakshatra: astroData.asc.ascendantNakshatra,
    ayanamsa: ayanamsa || 'Lahiri',
    birthDate: utcDateTime,
    location: selectedCity?.label,
    gender,
    name,
    ageYears
  };

  // ===== PLANETS =====
  const planets: ChatContext['planets'] = [];
  const planetOrder: PlanetName[] = ['Sun', 'Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn', 'Rahu', 'Ketu'];
  
  planetOrder.forEach(pName => {
    const placement = astroData.placements[pName];
    if (!placement) return;

    let lon = 0;
    let retrograde = false;

    if (pName === 'Rahu') {
      lon = astroData.nodes.Rahu;
    } else if (pName === 'Ketu') {
      lon = astroData.nodes.Ketu;
    } else {
      const grahaData = astroData.grahas[pName];
      if (grahaData) {
        lon = grahaData.lon;
        retrograde = grahaData.retrograde;
      }
    }

    const sign = getSignFromLongitude(lon);
    const nakshatra = getNakshatraFromLongitude(lon);
    const ruledHouses = getHousesRuledByPlanet(pName, astroData.asc.ascendantSignIndex);

    // Natal dignity (full 7-tier: own/moolatrikona/exalted/friend/neutral/enemy/debilitated)
    let natalDignity: string | undefined;
    if (lon !== 0 && pName !== 'Rahu' && pName !== 'Ketu') {
      try { natalDignity = getPlanetDignity(pName, lon); } catch { /* skip */ }
    }

    // Get planet-in-house interpretation if available
    let interpretation: string | undefined;
    if (includeInterpretations && houseInterpretations) {
      const houseInterp = houseInterpretations.find(hi => hi.house === placement.house);
      if (houseInterp?.planets) {
        const planetInterp = houseInterp.planets.find(p => p.planet === pName);
        if (planetInterp?.interpretation) {
          interpretation = truncateText(planetInterp.interpretation, maxInterpretationLength);
        }
      }
    }

    planets.push({
      name: pName,
      house: placement.house,
      sign,
      longitude: lon,
      nakshatra,
      retrograde,
      blendedStrength: blendedPlanetPct?.[pName],
      combust: combust?.[pName]?.combust || false,
      debilitated: debil?.[pName] || false,
      exalted: exalt?.[pName] || false,
      natalDignity,
      rules: ruledHouses,
      interpretation
    });
  });

  // ===== HOUSES =====
  const houses: ChatContext['houses'] = [];
  
  for (let h = 1; h <= 12; h++) {
    const cuspLon = astroData.cusps[h - 1];
    const sign = getSignFromLongitude(cuspLon);
    const signName = RASHI_NAMES[sign - 1] || 'Unknown';
    const lord = SIGN_LORDS[sign];
    const lordPlacement = astroData.placements[lord];
    
    // Find resident planets
    const residentPlanets = planets
      .filter(p => p.house === h)
      .map(p => p.name);
    
    // Find aspecting planets
    const aspectingPlanets = astroData.aspects
      .filter(a => a.toHouse === h)
      .map(a => a.from);

    // Get house interpretation if available (filtered by intent)
    let overview: string | undefined;
    let signNote: string | undefined;
    let planetInterpretations: Array<{ 
      planet: string; 
      text: string;
      nakshatraNote?: string;
      housePlanetNote?: string;
    }> | undefined;
    let lordshipInterpretations: Array<{ 
      lord: string; 
      position: string; 
      text: string;
      overview?: string;
      strengths?: string;
      challenges?: string;
      remedies?: string;
    }> | undefined;
    
    if (includeInterpretations) {
      const houseInterp = houseInterpretations?.find(hi => hi.house === h);
      if (houseInterp) {
        // Filter by intent or life area if specified
        if (!intent && !opts.lifeArea || 
            (intent && shouldIncludeHouseForIntent(h, intent, opts.lifeArea)) ||
            (opts.lifeArea && shouldIncludeHouseForIntent(h, '', opts.lifeArea))) {
          // House overview
          if (houseInterp.overview) {
            overview = truncateText(houseInterp.overview, maxInterpretationLength);
          }
          
          // Sign-in-house note
          if (houseInterp.signNote) {
            signNote = truncateText(houseInterp.signNote, maxInterpretationLength);
          }
          
          // Planet-in-house predictions WITH nakshatra notes
          if (houseInterp.planets && houseInterp.planets.length > 0) {
            planetInterpretations = houseInterp.planets
              // Include if ANY field has content (not just interpretation)
              .filter(p => p.interpretation || p.nakshatraNote || p.housePlanetNote)
              .map(p => ({
                planet: p.planet,
                text: truncateText(p.interpretation || '', maxInterpretationLength) || '',
                nakshatraNote: p.nakshatraNote ? truncateText(p.nakshatraNote, maxInterpretationLength) : undefined,
                housePlanetNote: p.housePlanetNote ? truncateText(p.housePlanetNote, maxInterpretationLength) : undefined
              }));
          }
          
          // Lord-in-house predictions WITH all metadata
          if (houseInterp.lordship && houseInterp.lordship.length > 0) {
            lordshipInterpretations = houseInterp.lordship
              // Include if ANY field has content
              .filter(l => l.interpretation || l.overview || l.strengths || l.challenges || l.remedies)
              .map(l => ({
                lord: l.lord,
                position: l.position,
                text: truncateText(l.interpretation || '', maxInterpretationLength) || '',
                overview: l.overview ? truncateText(l.overview, maxInterpretationLength) : undefined,
                strengths: l.strengths ? truncateText(l.strengths, maxInterpretationLength) : undefined,
                challenges: l.challenges ? truncateText(l.challenges, maxInterpretationLength) : undefined,
                remedies: l.remedies ? truncateText(l.remedies, maxInterpretationLength) : undefined
              }));
          }
        }
      }
    }

    houses.push({
      house: h,
      sign,
      signName,
      strength: bhavaBalaRupas?.[h] ?? d1HouseScores?.[h],
      residentPlanets,
      aspectingPlanets,
      lord,
      lordHouse: lordPlacement?.house,
      overview,
      signNote,
      planetInterpretations,
      lordshipInterpretations
    });
  }

  // ===== DASHAS =====
  let dashas: ChatContext['dashas'] | undefined;
  if (dashaContext) {
    // Check if it's the DashaContext format (with levels array)
    if ('levels' in dashaContext && Array.isArray(dashaContext.levels)) {
      const levels = dashaContext.levels;
      dashas = {
        MD: {
          planet: levels[0].planet,
          occupies: levels[0].occupies,
          rules: levels[0].rules,
          aspects: levels[0].aspects || [],
          from: levels[0].from,
          to: levels[0].to,
          transitStats: levels[0].transitStats
        },
        AD: {
          planet: levels[1].planet,
          occupies: levels[1].occupies,
          rules: levels[1].rules,
          aspects: levels[1].aspects || [],
          from: levels[1].from,
          to: levels[1].to,
          transitStats: levels[1].transitStats
        },
        PD: {
          planet: levels[2].planet,
          occupies: levels[2].occupies,
          rules: levels[2].rules,
          aspects: levels[2].aspects || [],
          from: levels[2].from,
          to: levels[2].to
        },
        SD: {
          planet: levels[3].planet,
          occupies: levels[3].occupies,
          rules: levels[3].rules,
          aspects: levels[3].aspects || [],
          from: levels[3].from,
          to: levels[3].to
        },
        window: dashaContext.window
      };
    } else {
      // It's the custom format from App.tsx dashaContextForFeeds
      const ctx = dashaContext as any;
      dashas = {
        MD: {
          planet: ctx.MD.planet,
          occupies: ctx.MD.occupies || ctx.MD.house,
          rules: ctx.MD.rules,
          aspects: ctx.MD.aspects || [],
          from: ctx.MD.from,
          to: ctx.MD.to,
          transitStats: ctx.MD.transitStats
        },
        AD: {
          planet: ctx.AD.planet,
          occupies: ctx.AD.occupies || ctx.AD.house,
          rules: ctx.AD.rules,
          aspects: ctx.AD.aspects || [],
          from: ctx.AD.from,
          to: ctx.AD.to,
          transitStats: ctx.AD.transitStats
        },
        PD: {
          planet: ctx.PD.planet,
          occupies: ctx.PD.occupies || ctx.PD.house,
          rules: ctx.PD.rules,
          aspects: ctx.PD.aspects || [],
          from: ctx.PD.from,
          to: ctx.PD.to
        },
        SD: {
          planet: ctx.SD.planet,
          occupies: ctx.SD.occupies || ctx.SD.house,
          rules: ctx.SD.rules,
          aspects: ctx.SD.aspects || [],
          from: ctx.SD.from,
          to: ctx.SD.to
        },
        window: ctx.window
      };
    }
  }

  // ===== FEEDS =====
  let contextFeeds: ChatContext['feeds'] | undefined;
  if (includeFeeds && feeds && feeds.length > 0) {
    // Filter by intent if specified
    contextFeeds = feeds
      .filter(f => !intent || shouldIncludeFeedForIntent(f, intent))
      .map(f => ({
        lifeArea: f.lifeArea,
        severity: f.severity,
        score: f.score,
        message: f.message,
        window: f.window,
        ruleKey: f.ruleKey
      }));
  }

  // ===== ASPECTS =====
  // Include ALL aspected houses (not just planet-to-planet)
  const aspects: ChatContext['aspects'] = [];
  
  // Get Parashara aspect rules
  const aspectRules: Record<string, number[]> = {
    'Sun': [7], 'Moon': [7], 'Mercury': [7], 'Venus': [7],
    'Mars': [4, 7, 8],
    'Jupiter': [5, 7, 9],
    'Saturn': [3, 7, 10],
    'Rahu': [5, 7, 9],  // Nodes use 5,7,9 in most traditions
    'Ketu': [5, 7, 9]
  };
  
  // For each planet, calculate which houses it aspects
  planetOrder.forEach(pName => {
    const placement = astroData.placements[pName];
    if (!placement) return;
    
    const fromHouse = placement.house;
    const offsets = aspectRules[pName] || [7]; // Default to 7th aspect only
    
    offsets.forEach(offset => {
      let toHouse = fromHouse + offset - 1; // -1 because offset is 1-based (e.g., 7th = +6 houses)
      if (toHouse > 12) toHouse -= 12;
      if (toHouse < 1) toHouse += 12;
      
      aspects.push({
        from: pName,
        toHouse: toHouse
      });
    });
  });

  // ===== TRANSITS =====
  let transits: ChatContext['transits'] | undefined;
  if (data.transitData) {
    const transitPlanets: Array<{
      name: string;
      house: number;
      sign: number;
      longitude: number;
      natalHouse: number;
      transitDignity?: string;
      retrograde?: boolean;
      combust?: boolean;
    }> = [];
    
    planetOrder.forEach(pName => {
      const transitPlacement = data.transitData!.placements[pName];
      const transitLon = data.transitData!.longitudes[pName];
      const natalPlacement = astroData.placements[pName];
      
      if (transitPlacement && typeof transitLon === 'number' && natalPlacement) {
        const transitSign = getSignFromLongitude(transitLon);
        transitPlanets.push({
          name: pName,
          house: transitPlacement.house,
          sign: transitSign,
          longitude: transitLon,
          natalHouse: natalPlacement.house,
          transitDignity: data.transitData!.dignities?.[pName],
          retrograde: data.transitData!.retrogrades?.[pName] ?? false,
          combust:    data.transitData!.combust?.[pName]     ?? false,
        });
      }
    });
    
    if (transitPlanets.length > 0) {
      transits = {
        date: data.transitData.date,
        planets: transitPlanets,
        // Sign ingress/egress windows — forwarded from ChatSourceData
        sunSignDates:     data.transitData.sunSignDates,
        marsSignDates:    data.transitData.marsSignDates,
        mercurySignDates: data.transitData.mercurySignDates,
        venusSignDates:   data.transitData.venusSignDates,
        jupiterSignDates: data.transitData.jupiterSignDates,
        saturnSignDates:  data.transitData.saturnSignDates,
        rahuSignDates:    data.transitData.rahuSignDates,
        ketuSignDates:    data.transitData.ketuSignDates,
      };
    }
  }

  // ===== SADE SATI =====
  let sadeSati: ChatContext['sadeSati'] | undefined;
  if (data.sadeSati) {
    sadeSati = {
      isActive: data.sadeSati.isActive,
      phase: data.sadeSati.currentPhase,
      from: data.sadeSati.from,
      to: data.sadeSati.to,
      moonSign: data.sadeSati.moonSign,
      saturnSign: data.sadeSati.saturnSign,
      allPhases: data.sadeSati.allPhases,
      totalFrom: data.sadeSati.totalFrom,
      totalTo: data.sadeSati.totalTo
    };
  }

  // ===== KAAL SARP YOGA =====
  let kaalSarpYoga: ChatContext['kaalSarpYoga'] | undefined;
  if (data.kaalSarpYoga) {
    kaalSarpYoga = {
      isPresent: data.kaalSarpYoga.isPresent,
      type: data.kaalSarpYoga.type,
      details: data.kaalSarpYoga.details,
      specificType: data.kaalSarpYoga.specificType,
      specificEffects: data.kaalSarpYoga.specificEffects,
      rahuHouse: data.kaalSarpYoga.rahuHouse,
      ketuHouse: data.kaalSarpYoga.ketuHouse,
      planetsBetween: data.kaalSarpYoga.planetsBetween,
      hasRajYogaCombination: data.kaalSarpYoga.hasRajYogaCombination
    };
  }

  // ===== CONJUNCTIONS =====
  const conjunctions: ChatContext['conjunctions'] = data.conjunctions?.filter(c => c.planets.length >= 2) ?? [];

  // ===== HOUSE AFFLICTIONS (lord dusthana penalties) =====
  let houseAfflictions: ChatContext['houseAfflictions'] | undefined;
  if (data.lordDusthanaPenalties && Object.keys(data.lordDusthanaPenalties).length > 0) {
    houseAfflictions = Object.entries(data.lordDusthanaPenalties).map(([h, info]) => ({
      house: Number(h),
      lord: (info as any).lord,
      lordHouse: (info as any).lordHouse,
      penalty: (info as any).penalty,
      severity: (info as any).severity,
      reason: (info as any).reason,
      jupiterProtected: (info as any).jupiterProtected || undefined,
    }));
  }

  // ===== FUNCTIONAL NATURE =====
  let functionalNature: ChatContext['functionalNature'] | undefined;
  if (data.functionalNature) {
    const benefics: string[] = [];
    const malefics: string[] = [];
    const yogaKarakas: string[] = [];
    
    Object.entries(data.functionalNature).forEach(([planet, nature]) => {
      if (nature === 'benefic') benefics.push(planet);
      else if (nature === 'malefic') malefics.push(planet);
      else if (nature === 'yoga_karaka') yogaKarakas.push(planet);
    });
    
    functionalNature = { benefics, malefics, yogaKarakas };
  }

  // ===== CHARA KARAKAS =====
  let charaKarakas: ChatContext['charaKarakas'] | undefined;
  const ckRaw = computeCharaKarakas(astroData.grahas);
  if (ckRaw && Object.keys(ckRaw).length === 7) {
    charaKarakas = {
      AK: ckRaw.AK,
      AmK: ckRaw.AmK,
      BK: ckRaw.BK,
      MK: ckRaw.MK,
      PuK: ckRaw.PuK,
      GK: ckRaw.GK,
      DK: ckRaw.DK
    };
  }

  // ===== DIVISIONAL CHARTS (VARGAS) =====
  let vargas: ChatContext['vargas'] | undefined;
  let lifeAreaContext: ChatContext['lifeAreaContext'] | undefined;
  
  // Process varga data if life area is specified or if explicitly requested
  if ((opts.lifeArea || opts.includeVargas) && data.vargaData) {
    // Determine which vargas to include
    let relevantVargas: VargaKey[] = [];
    if (opts.lifeArea) {
      const lifeArea = LIFE_AREAS[opts.lifeArea];
      // Life-area vargas at full relevance + D9/D10 always (marriage/dharma + career
      // cross-confirmation is required for holistic synthesis regardless of question)
      relevantVargas = Array.from(new Set<VargaKey>([...lifeArea.vargas, 'D9' as VargaKey, 'D10' as VargaKey]));
      
      // Set life area context
      lifeAreaContext = {
        area: opts.lifeArea,
        label: lifeArea.label,
        description: lifeArea.description,
        relevantHouses: lifeArea.houses,
        relevantVargas: lifeArea.vargas
      };
    } else if (opts.includeVargas) {
      // Include commonly used vargas if no specific life area
      relevantVargas = ['D1', 'D9', 'D10'] as VargaKey[];
    }
    
    vargas = [];
    relevantVargas.forEach(vargaKey => {
      const vargaData = data.vargaData![vargaKey];
      if (!vargaData) return;
      
      const planets: Array<{
        name: string;
        house: number;
        sign: number;
        signName: string;
        dignity?: string;
      }> = [];
      
      // Build planet list for this varga
      planetOrder.forEach(pName => {
        const placement = vargaData.placements[pName];
        const sign = vargaData.planetSigns[pName];
        
        if (placement && sign) {
          // Compute representative longitude for varga sign (midpoint of sign)
          const signLon = (sign - 1) * 30 + 15;
          let varDignity: string | undefined;
          try {
            varDignity = getPlanetDignity(pName, signLon);
          } catch {
            varDignity = undefined;
          }
          planets.push({
            name: pName,
            house: placement.house,
            sign: sign,
            signName: RASHI_NAMES[sign - 1] || 'Unknown',
            dignity: varDignity
          });
        }
      });
      
      vargas!.push({
        chart: vargaKey,
        description: getVargaDescription(vargaKey),
        ascSign: vargaData.ascSign,
        ascSignName: RASHI_NAMES[vargaData.ascSign - 1] || 'Unknown',
        planets
      });
    });
  }

  return {
    meta,
    planets,
    houses,
    dashas,
    feeds: contextFeeds,
    aspects,
    transits,
    sadeSati,
    kaalSarpYoga,
    conjunctions: conjunctions && conjunctions.length > 0 ? conjunctions : undefined,
    houseAfflictions,
    charaKarakas,
    functionalNature,
    vargas,
    lifeAreaContext,
    remedies: lifeAreaRemedies, // Include remedies if provided
    combinationPredictions: data.combinationPredictions,
    dashaLordRemedies: data.dashaLordRemedies,
    dashaImpactByHouse: data.dashaImpactByHouse,
    transitImpactByHouse: data.transitImpactByHouse,
    frictionScoresByPlanet: data.frictionScoresByPlanet,
    houseActivities: data.houseActivities,
  };
}

// ===== INTENT FILTERING HELPERS =====

function shouldIncludeHouseForIntent(house: number, intent: string, lifeArea?: LifeAreaKey): boolean {
  // If life area is specified, use its house list
  if (lifeArea) {
    const area = LIFE_AREAS[lifeArea];
    return area?.houses.includes(house) ?? true;
  }
  
  // Otherwise use intent-based filtering
  const intentHouseMap: Record<string, number[]> = {
    career: [1, 6, 10, 11], // Self, service, career, gains
    health: [1, 6, 8, 12], // Self, disease, longevity, hospitalization
    relationships: [1, 5, 7, 8, 11], // Self, romance, spouse, intimacy, friends
    wealth: [2, 5, 9, 11], // Wealth, investments, fortune, gains
    dasha: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12], // All houses (dasha affects all)
    transit: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12], // All houses
    general: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12] // All houses
  };
  
  return intentHouseMap[intent]?.includes(house) ?? true;
}

function shouldIncludeFeedForIntent(feed: FeedItem, intent: string): boolean {
  const intentLifeAreaMap: Record<string, string[]> = {
    career: ['Career', 'Wealth', 'Investments'],
    health: ['Health', 'AccidentRisk'],
    relationships: ['Spouse', 'Children', 'Mother', 'Father'],
    wealth: ['Wealth', 'Investments', 'Career'],
    dasha: ['Self', 'Career', 'Wealth', 'Health', 'Spouse', 'Children'], // Core areas
    transit: ['Self', 'Career', 'Wealth', 'Health', 'Spouse', 'Children'],
    general: [] // Include all
  };
  
  const allowedAreas = intentLifeAreaMap[intent];
  if (!allowedAreas || allowedAreas.length === 0) return true; // Include all for general
  
  return allowedAreas.includes(feed.lifeArea);
}

// ===== CONTEXT SIZE ESTIMATION =====

export function estimateContextTokens(context: ChatContext): number {
  // Rough estimation: 1 token ≈ 4 characters
  const jsonString = JSON.stringify(context);
  return Math.ceil(jsonString.length / 4);
}

// ===== EXPORT =====
// ChatContext type already exported above
// ===== END FILE: src/utils/chatContext.ts =====
